/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {AuthInstance} from '@exc/auth';
import Core from './Core';
import CoreContext from './context';
import * as coreLocaleData from './__localization__/Core';
import {createBrowserHistory} from 'history';
import {getConfiguration} from './utils/config';
import {
  getCurrentTenantAndPath,
  getPathWithoutTenant,
  getQueryParams,
  getQueryValue,
  hashToPath
} from '@exc/url';
import {history, Route, Router} from '@exc/router';
import I18nProvider from '@adobe/adobe-intl-react';
import {Login, NotFound} from './imports/ImportManager';
import LoginBackground from './resources/bg-2048x1280-2020.jpg';
import metrics, {Events as MetricsEvents, MetricsRuntime} from '@adobe/exc-app/metrics';
import {preload} from '@exc/dom';
import Provider from '@react/react-spectrum/Provider';
import React from 'react';
import Sandbox from './modules/Sandbox';

export {CoreContext, getPathWithoutTenant, getQueryValue, NotFound, Sandbox};

export default class Main extends React.Component {
  constructor(props) {
    super(props);

    this.metrics = metrics.create('exc.core.App');
    this.shellTimer = this.metrics.start('Shell');

    let imsEnv = getQueryValue('ims');
    this.config = getConfiguration(props, imsEnv);
    let mode = getQueryValue('metricsmode') || (this.config.environment === 'prod' && 'quiet');

    this.env = this.config.ims.environment || this.config.environment;
    mode && MetricsRuntime.configure({mode});
    this.metrics.event(MetricsEvents.PAGE_LOAD_START, 'App constructed');

    if (window._srp && window._srp.resourceURL && window._srp.resourceURL !== this.config.cdn) {
      this.metrics.event('Shell.CDNFallback', `Using ${window._srp.resourceURL} for static assets.`);
      this.config.cdn = window._srp.resourceURL;
    }

    this.preloader = preload(document, this.config);
    this.preloader.onNeed('start');

    this.state = {
      auth: null,
      authenticated: false,
      defaultComponent: this.config.defaultComponent,
      loading: true
    };

    // Auth history must be browser history in order to work due to how the URLs
    // and params are handled. `props.authHistory` is only used for testing.
    this.authHistory = props.authHistory || createBrowserHistory();
  }

  async componentDidMount() {
    const listeners = {
      'auth.accesstokenexpired': () => {
        this.metrics.event('Auth.userToken.fail', 'Access token expired, logged out');
        this.signOut(this.state.auth, () => {
          this.handleLoginRedirect(this.config.ims.redirect_uri);
          this.authHistory.go();
        });
      },
      'auth.validatetokenfailed': () => {
        this.metrics.event('Auth.userToken.fail', 'User token validation failed, logged out');
        this.signOut(this.state.auth, () => {
          this.handleLoginRedirect(this.config.ims.redirect_uri);
          this.authHistory.go();
        });
      }
    };

    this.metrics.event('Auth.start', getCurrentTenantAndPath());
    const auth = await AuthInstance({
      // If the envs are the same, use the current environment. Otherwise use
      // the previous environment so that sign out can occur.
      env: this.env,
      imsConfig: Object.assign({}, this.config.ims),
      listeners
    });

    let customToken = getQueryValue('customtoken');
    // Only allow use of custom tokens on QA and while E2E testing with Cypress.
    // There is a security vulnerability by allowing tokens in query params,
    // whereby tokens can end up in logs and potentially be leaked to 3rd parties.
    if (customToken) {
      if (['dev', 'qa'].includes(this.config.environment)) {
        await auth.setCustomToken(customToken);
      } else {
        customToken = false;
      }
    }

    this.shellTimer.time('imslib.loaded');

    let isLogin = false;
    if (auth.isAuthenticated(!!customToken)) {
      if (this.authHistory.location.pathname === '/login') {
        this.authHistory.push('/#/home');
      } else if (history.location.pathname.indexOf('from_ims') > -1) {
        // We need to check for a valid session state to prevent CSRF issues.
        if (!auth.isValidSession()) {
          this.metrics.event('Auth.csrf.fail', 'Auth state param didn\'t match');
          return auth.signOut();
        }
        // This is the trigger that can be used to
        // replace the history with the hash of the login history. The history types
        // are different because login uses browser history while the app uses hash
        // history.
        let {hash} = window.location;
        const params = getQueryParams(hashToPath({hash, removePrecedingSlash: true}));
        if (params.old_hash) {
          hash = decodeURIComponent(params.old_hash);
        }
        history.replace(hashToPath({hash}));
        isLogin = true;
      }

      // User is authenticated and loading is complete.
      this.preloader.onNeed('auth');

      return this.setState({auth, authenticated: true, isLogin, loading: false});
    }

    this.preloader.onNeed('login');
    this.preloader.asset(LoginBackground, 'image');

    this.setState({auth, loading: false});
    auth.signOut();
    this.metrics.event('Login.imsSSO.ready', 'Invalid session: imsSSO login required');
    this.handleLoginRedirect(this.config.ims.redirect_uri);
  }

  handleLoginRedirect(referrerUri) {
    let query = '';

    if (!/^\/login.*$/.test(this.authHistory.location.pathname)) {
      query += `redirectlocation=${encodeURIComponent(referrerUri)}`;
      const referrerURL = referrerUri && new URL(referrerUri);
      const referrerSearchParams = referrerURL && referrerURL.searchParams;
      if (referrerSearchParams && referrerSearchParams.toString()) {
        query += `&${referrerSearchParams.toString()}`;
      }
    }
    this.authHistory.replace(`/login?${query}`);
  }

  signOut(auth, handler) {
    window.adobeMetrics.clearUser();
    auth.clear();
    return this.setState({auth}, handler);
  }

  renderApp() {
    this.metrics.analytics.trackPage();

    return (
      <I18nProvider autoLocale localeData={coreLocaleData}>
        <Core
          config={this.config}
          isLogin={this.state.isLogin}
          listedApps={this.props.listedApps}
          metricsEnv={this.props.metricsEnv}
          shellTimer={this.shellTimer}>
          {this.props.children}
        </Core>
      </I18nProvider>
    );
  }

  renderAuth() {
    this.metrics.analytics.trackPage();

    return (
      <React.Suspense fallback={this.state.defaultComponent}>
        <I18nProvider autoLocale localeData={Login.loginLocaleData}>
          <Router history={this.authHistory}>
            <Route path="/login(.*)">
              <Login />
            </Route>
            <Route path="(.*)">{this.state.defaultComponent}</Route>
          </Router>
        </I18nProvider>
      </React.Suspense>
    );
  }

  render() {
    const {auth, authenticated, defaultComponent, loading} = this.state;

    if (!auth) {
      return <Provider>{defaultComponent}</Provider>;
    }

    // Determines if auth should be loaded or the app. /login happens when user
    // is both authenticated and not authenticated. Specifically, the user can
    // be authenticated with /login in the post login workflow.
    const isLoginPath = /^\/login/.test(this.authHistory.location.pathname);
    const isAuth = isLoginPath || !authenticated || loading;
    return (
      <Provider locale={this.state.locale} toastPlacement="top center">
        {isAuth ? this.renderAuth() : this.renderApp()}
      </Provider>
    );
  }
}
